import { DragIndicator, Delete } from '@mui/icons-material';
import {
  Box,
  Chip,
  Switch,
  Typography,
  IconButton,
  Button,
  Tooltip
} from '@mui/material';
import React, { useState } from 'react';

import { ApprovalMode, ApprovalRule } from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import {
  getApprovalModeLabel,
  getApproversText,
  getApprovalRuleConditionText
} from 'utils/helpers/approvalsHelpers';

import {
  dotSeparatorStyle,
  approversTextStyle
} from 'styles/components/Common';
import {
  rowBaseStyle,
  dragHandleStyle,
  deleteButtonStyle,
  cancelButtonStyle,
  getChipStyle,
  chipExclusiveStyle,
  chipGrayStyle,
  rulePriorityStyle,
  ruleNameStyle,
  ruleContentBoxStyle,
  ruleHeaderBoxStyle,
  conditionWrapperStyle,
  conditionLabelStyle,
  conditionBoxStyle,
  deleteConfirmationBoxStyle,
  defaultRuleContentStyle
} from 'styles/pages/ApprovalsStyle';

import { useDragContext } from '../context/DragContext';

interface ApprovalRuleRowProps {
  rule: ApprovalRule;
  isDefault?: boolean;
  isFirst?: boolean;
  isLast?: boolean;

  // simplified callbacks
  onToggleActive?: (ruleId: number, isActive: boolean) => Promise<void>;
  onDelete?: (id: number) => void;
  onClick?: () => void;
}

export const ApprovalRuleRow = ({
  rule,
  isDefault = false,
  isFirst = false,
  isLast = false,
  onToggleActive,
  onDelete,
  onClick
}: ApprovalRuleRowProps) => {
  const { translate } = useTranslations();
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  const {
    draggedId,
    dragOverId,
    setDraggedId,
    setDragOverId,
    onDrop: handleDropGlobal
  } = useDragContext();

  const isSequential = rule.flow?.approvalMode === ApprovalMode.SEQUENTIAL;
  const ruleId = rule.id ?? -1;

  const handleToggleActive = async () => {
    if (!onToggleActive) return;
    await onToggleActive(ruleId, !rule.active);
  };

  const handleToggleActiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleToggleActive();
  };

  const handleStartDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsConfirmingDelete(true);
  };

  const handleCancelDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsConfirmingDelete(false);
  };

  const handleConfirmDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!onDelete) return;
    onDelete(ruleId);
    setIsConfirmingDelete(false);
  };

  const handleDragStart = (e: React.DragEvent) => {
    if (isDefault) return;
    e.dataTransfer.setData('text/plain', ruleId.toString());
    e.dataTransfer.effectAllowed = 'move';
    setDraggedId(ruleId);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (isDefault) return;
    e.preventDefault();
    if (draggedId === ruleId) return;
    setDragOverId(ruleId);
  };

  const handleDragLeave = () => {
    setDragOverId(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const draggedIdStr = e.dataTransfer.getData('text/plain');
    const sourceId = Number.parseInt(draggedIdStr, 10);

    if (sourceId === ruleId || isDefault) return;

    handleDropGlobal?.(sourceId, ruleId);
  };

  const renderDragHandle = () => {
    if (isDefault) return null;
    return <DragIndicator sx={dragHandleStyle} />;
  };

  const renderPriority = () => {
    if (isDefault) return null;
    return <Typography sx={rulePriorityStyle}>{rule.priority}</Typography>;
  };

  const renderRuleChips = () => {
    if (!rule.flow) return null;

    return (
      <>
        <Chip
          size="small"
          label={getApprovalModeLabel(rule.flow?.approvalMode, translate)}
          sx={getChipStyle(rule.flow?.approvalMode)}
        />
        {rule.exclusive && !isDefault && (
          <Chip
            size="small"
            label={translate('approvals.exclusive')}
            sx={chipExclusiveStyle}
          />
        )}
        {isDefault && (
          <Chip
            size="small"
            label={translate('approvals.cannotBeDeleted')}
            sx={{
              fontSize: '0.625rem',
              fontWeight: 600,
              height: '1.125rem',
              borderRadius: '0.1875rem',
              backgroundColor: 'white',
              color: '#9ca3af',
              border: '1px solid #e5e7eb'
            }}
          />
        )}
        <Box sx={dotSeparatorStyle} />
        {rule.flow.approvalMode === ApprovalMode.PARALLEL && (
          <Chip
            size="small"
            label={`${rule.flow?.minimumApprovalsRequired} of ${rule.flow?.steps?.length}`}
            sx={chipGrayStyle}
          />
        )}
        {(isSequential || rule.flow.approvalMode === ApprovalMode.PARALLEL) &&
          rule.flow.steps && (
            <Typography variant="body2" sx={approversTextStyle}>
              {getApproversText(rule.flow?.steps, rule.flow?.approvalMode)}
            </Typography>
          )}
      </>
    );
  };

  const renderConditions = () => {
    if (!rule.conditions || rule.conditions.length === 0) return null;

    return (
      <Box sx={conditionWrapperStyle}>
        <Typography sx={conditionLabelStyle}>
          {translate('approvals.if')}
        </Typography>
        {rule.conditions?.map((c, idx) => (
          <React.Fragment key={`condition-${c.conditionType}-${idx}`}>
            <Box sx={conditionBoxStyle}>
              <Typography
                sx={{
                  color: '#6b7280',
                  fontSize: '0.625rem',
                  lineHeight: 1.4
                }}
              >
                {getApprovalRuleConditionText(c, translate)}
              </Typography>
            </Box>
            {idx < (rule.conditions?.length ?? 0) - 1 && (
              <Typography sx={conditionLabelStyle}>
                {translate('approvals.and')}
              </Typography>
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  const renderDefaultDescription = () => {
    if (!isDefault) return null;
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          mt: '0.1875rem',
          flexWrap: 'wrap'
        }}
      >
        <Typography
          sx={{
            color: '#6b7280',
            fontSize: '0.625rem',
            lineHeight: 1.4
          }}
        >
          {translate('approvals.catchesAllUnmatchedDocuments')}
        </Typography>
      </Box>
    );
  };

  const renderActions = () => {
    if (isDefault) return null;

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
      >
        <Switch
          checked={rule.active ?? false}
          onChange={handleToggleActive}
          onClick={handleToggleActiveClick}
          color="primary"
          size="small"
        />
        {isConfirmingDelete ? (
          <Box sx={deleteConfirmationBoxStyle}>
            <Button
              onClick={handleConfirmDelete}
              size="small"
              variant="contained"
              disableElevation
              sx={deleteButtonStyle}
            >
              {translate('labels.delete')}
            </Button>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              color="primary"
              size="small"
              disableElevation
              sx={cancelButtonStyle}
            >
              {translate('labels.cancel')}
            </Button>
          </Box>
        ) : (
          <Tooltip title={translate('approvals.deleteRule')}>
            <IconButton
              onClick={handleStartDelete}
              size="small"
              aria-label="delete"
              sx={{
                color: '#d1d5db',
                '&:hover': {
                  color: '#ef4444'
                }
              }}
            >
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  if (isDefault) {
    return (
      <Box sx={defaultRuleContentStyle} onClick={onClick}>
        <Box sx={ruleContentBoxStyle}>
          <Box sx={ruleHeaderBoxStyle}>
            <Typography
              variant="body2"
              sx={{
                ...ruleNameStyle,
                ...(isDefault && { color: '#374151' })
              }}
            >
              {rule.name}
            </Typography>
            {renderRuleChips()}
          </Box>
          {renderDefaultDescription()}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      draggable={!isDefault}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={onClick}
      sx={{
        ...rowBaseStyle,
        ...(isFirst && {
          borderTopLeftRadius: '0.375rem',
          borderTopRightRadius: '0.375rem'
        }),
        ...(isLast && {
          borderBottomLeftRadius: '0.375rem',
          borderBottomRightRadius: '0.375rem'
        }),
        borderTop: isFirst ? 'none' : '1px solid #e0e0e0',
        boxShadow:
          dragOverId === rule.id ? 'inset 0.125rem 0 0 0 #078DEE' : 'none',
        backgroundColor: draggedId === ruleId ? '#f5f5f5' : 'white',
        ...(draggedId !== ruleId && {
          '&:hover': {
            backgroundColor: '#f9f9f9'
          }
        }),
        '&:active': { cursor: 'grabbing' },
        opacity: !rule.active || draggedId === ruleId ? 0.5 : 1
      }}
    >
      {renderDragHandle()}
      {renderPriority()}
      <Box sx={ruleContentBoxStyle}>
        <Box sx={ruleHeaderBoxStyle}>
          <Typography variant="body2" sx={ruleNameStyle}>
            {rule.name}
          </Typography>
          {renderRuleChips()}
        </Box>
        {renderConditions()}
      </Box>
      {renderActions()}
    </Box>
  );
};
